import { useMemo, useRef, useState } from "react";
import {
  Box,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";
import { Bar } from "react-chartjs-2";
import { useQuery } from "@apollo/client";
import { endOfDay, format, startOfDay } from "date-fns";
import { IDayStats, METRICS, formatRange, sumTotals } from "./types";
import DetailsDialog, { DetailsRequest } from "./DetailsDialog";
import { buildSummaryExport, chartOptions, exportSummaryExcel, exportSummaryPdf, getChartData } from "./exportSummary";
import { GETPROJECTCALLLOG, ICallLogRow } from "./CallsDetails";
import { countPrimaryObjections } from "./objections";

const NAVY = "#17324d";

const headCell = { backgroundColor: NAVY, color: "#fff", fontWeight: 700, whiteSpace: "nowrap" as const, py: 1.25 };
const bodyCell = { py: 0.5, borderRight: "1px solid #e0e0e0", whiteSpace: "nowrap" as const };

// Non zero values are bold and open the details modal
const Value = (props: { value: number; onClick: () => void }) => {
  const { value, onClick } = props;
  if (!value) return <>0</>;
  return (
    <Box component="strong" onClick={onClick} sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
      {value}
    </Box>
  );
};

const OverallSummary = (props: ChildProps) => {
  const { projectId, projectName, dateStart, dateEnd, days, loading } = props;
  const [request, setRequest] = useState<DetailsRequest | null>(null);
  const [exporting, setExporting] = useState<boolean>(false);
  const chartRef = useRef<any>(null);

  const totals = useMemo(() => sumTotals(days.map((day) => day.totals)), [days]);

  // Primary Objection counts over the Details tab's calls, same as the project report. Loaded separately because
  // the calls come from Twilio and should not hold up the rest of the dashboard
  const callStart = useMemo(() => startOfDay(dateStart), [dateStart]);
  const callEnd = useMemo(() => endOfDay(dateEnd), [dateEnd]);
  const { data: callData, loading: callsLoading, error: callsError } = useQuery(GETPROJECTCALLLOG, {
    fetchPolicy: "network-only",
    skip: !projectId,
    variables: { project: projectId, dateStart: callStart, dateEnd: callEnd },
  });
  const calls: ICallLogRow[] = useMemo(() => callData?.projectCallLog || [], [callData]);
  const objectionCounts = useMemo(() => countPrimaryObjections(calls, callStart, callEnd), [calls, callStart, callEnd]);

  const summaryRows: { label: string; value: React.ReactNode }[] = METRICS.map((metric) => ({
    label: metric.label,
    value: <Value value={totals[metric.key]} onClick={() => setRequest({ type: "metric", metric, dateStart, dateEnd })} />,
  }));

  const range = formatRange(dateStart, dateEnd);

  const handleExport = async (type: "pdf" | "excel") => {
    const data = buildSummaryExport(projectName, range, days, chartRef.current?.toBase64Image());
    const fileName = `${projectName} User Dashboard ${range}`;
    setExporting(true);
    try {
      if (type === "pdf") await exportSummaryPdf(data, `${fileName}.pdf`);
      else await exportSummaryExcel(data, `${fileName}.xlsx`);
    } finally {
      setExporting(false);
    }
  };

  const chartData = getChartData(days);

  if (loading) {
    return (
      <Box sx={{ mt: 2 }}>
        <Skeleton height={60} animation="wave" />
        <Skeleton variant="rectangular" height={320} animation="wave" sx={{ mt: 1 }} />
        <Skeleton variant="rectangular" height={200} animation="wave" sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ backgroundColor: NAVY, color: "#fff", px: 1.5, pt: 3, pb: 1, mb: 2, display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 700, textTransform: "uppercase" }}>
          {projectName} | User Dashboard
        </Typography>
        <Box sx={{ display: "flex", flexShrink: 0 }}>
          <IconButton title="Download PDF" disabled={exporting} onClick={() => handleExport("pdf")} sx={{ color: "#fff" }}>
            <PictureAsPdfIcon />
          </IconButton>
          <IconButton title="Download Excel" disabled={exporting} onClick={() => handleExport("excel")} sx={{ color: "#fff" }}>
            <TableViewIcon />
          </IconButton>
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headCell}>Metric</TableCell>
                  <TableCell sx={headCell}>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryRows.map((row) => (
                  <TableRow key={row.label}>
                    <TableCell sx={bodyCell}>{row.label}</TableCell>
                    <TableCell sx={bodyCell} align="right">
                      {row.value}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid item xs={12} md={8}>
          <Box sx={{ border: "1px solid #e0e0e0", p: 2, height: { xs: 300, md: "100%" }, minHeight: 300, boxSizing: "border-box" }}>
            <Bar ref={chartRef} data={chartData} options={chartOptions} />
          </Box>
        </Grid>
      </Grid>

      <TableContainer sx={{ mt: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCell}>Date</TableCell>
              {METRICS.map(({ key, short }) => (
                <TableCell key={key} sx={headCell} align="right">
                  {short}
                </TableCell>
              ))}
              <TableCell sx={headCell}>Users</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {days.map((day) => (
              <TableRow key={day.date.toISOString()} hover>
                <TableCell sx={bodyCell}>{format(day.date, "MMM d, yyyy")}</TableCell>
                {METRICS.map((metric) => (
                  <TableCell key={metric.key} sx={bodyCell} align="right">
                    <Value
                      value={day.totals[metric.key]}
                      onClick={() => setRequest({ type: "metric", metric, dateStart: day.date, dateEnd: day.date })}
                    />
                  </TableCell>
                ))}
                <TableCell sx={{ ...bodyCell, whiteSpace: "normal" }}>{day.activeUsers.length ? day.activeUsers.join(", ") : "None"}</TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ "& td": { borderTop: `2px solid ${NAVY}` } }}>
              <TableCell sx={{ ...bodyCell, fontWeight: 700 }}>Total</TableCell>
              {METRICS.map((metric) => (
                <TableCell key={metric.key} sx={bodyCell} align="right">
                  <Value value={totals[metric.key]} onClick={() => setRequest({ type: "metric", metric, dateStart, dateEnd })} />
                </TableCell>
              ))}
              <TableCell sx={bodyCell} />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <TableContainer sx={{ mt: 3, maxWidth: 520 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCell}>Primary Objection</TableCell>
              <TableCell sx={headCell} align="right">
                Count
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {callsLoading ? (
              <TableRow>
                <TableCell colSpan={2} sx={{ textAlign: "center", py: 2 }}>
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            ) : callsError ? (
              <TableRow>
                <TableCell colSpan={2} sx={{ color: "error.main" }}>
                  Error getting calls
                </TableCell>
              </TableRow>
            ) : (
              <>
                {objectionCounts.map(([objection, count]) => (
                  <TableRow key={objection} hover>
                    <TableCell sx={bodyCell}>{objection}</TableCell>
                    <TableCell sx={bodyCell} align="right">
                      {count ? <strong>{count}</strong> : 0}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ "& td": { borderTop: `2px solid ${NAVY}`, fontWeight: 700 } }}>
                  <TableCell sx={bodyCell}>Total</TableCell>
                  <TableCell sx={bodyCell} align="right">
                    {calls.length}
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <DetailsDialog project={projectId} request={request} onClose={() => setRequest(null)} />
    </Box>
  );
};

interface ChildProps {
  projectId: string;
  projectName: string;
  dateStart: Date;
  dateEnd: Date;
  days: IDayStats[];
  loading: boolean;
}

export default OverallSummary;
